import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { APPLICATION_CARDS } from "@/data/content";
import { useCatalog, getById } from "@/lib/catalog";
import { track } from "@/lib/api";
import { BuildingMesh } from "@/components/Vectors";
import { APP_ICONS } from "@/lib/icons";

export default function Applications() {
  const { products } = useCatalog();
  return (
    <div className="bg-white" data-testid="applications-page">
      <SEO
        title="Applications — Office, Industrial, High-Bay, Outdoor"
        description="Where Amaya Pro works: offices, commercial buildings, industrial facilities, high-bay, low-bay, outdoor and residential applications."
        path="/applications"
      />
      <PageHero
        dark
        overline="Applications"
        title="One system. Every space."
        sub="From open offices to 40 ft high-bay floors and outdoor perimeters — Amaya Pro has documented, application-ready controls."
        artwork={<BuildingMesh className="w-full h-auto" />}
      />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 space-y-6">
        {APPLICATION_CARDS.map((a, i) => {
          const prods = a.products.map((id) => getById(products, id)).filter(Boolean);
          return (
            <Reveal key={a.id}>
              <div id={a.id} className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-zinc-200 bg-white scroll-mt-24" data-testid={`application-${a.id}`}>
                <div className="lg:col-span-4 p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-zinc-200">
                  <div className="flex items-center gap-3">
                    {(() => { const AppIcon = APP_ICONS[a.id]; return AppIcon ? <AppIcon size={24} className="text-signal" strokeWidth={1.6} /> : null; })()}
                    <span className="font-mono text-sm text-signal">/{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3">{a.title}</h2>
                  <div className="mt-6">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">The challenge</p>
                    <p className="text-sm text-zinc-500 leading-relaxed">{a.challenge}</p>
                  </div>
                  <div className="mt-5">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">How Amaya helps</p>
                    <p className="text-sm text-ink leading-relaxed">{a.answer}</p>
                  </div>
                  <Link
                    to="/contact?type=demo"
                    onClick={() => track("cta_click", { cta: "discuss_project", location: `applications:${a.id}` })}
                    className="inline-flex items-center gap-2 mt-7 text-sm font-medium text-signal hover:gap-3 transition-all"
                    data-testid={`app-cta-${a.id}`}
                  >
                    Design Your Lighting Control System <ArrowRight size={15} />
                  </Link>
                </div>
                <div className="lg:col-span-8 p-8 md:p-10">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-5">Recommended components</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                    {prods.map((p) => (
                      <Link
                        key={p.id}
                        to={`/products/${p.id}`}
                        className="group bg-bone border border-zinc-200 hover:border-signal transition-colors p-4 flex flex-col"
                        data-testid={`app-product-${p.id}`}
                      >
                        <div className="h-24 flex items-center justify-center mb-3">
                          <img src={p.image} alt={p.name} loading="lazy" className="max-h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105" />
                        </div>
                        <span className="font-mono text-[10px] text-signal">№ {p.orderNumber}</span>
                        <span className="text-xs text-ink mt-1 leading-snug flex-1">{p.name}</span>
                        <span className="text-[10px] text-zinc-500 mt-2 flex items-center gap-1">View <ArrowUpRight size={10} /></span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </section>
      <CTASection title="Discuss your project." sub="Tell us the space — we'll recommend the system." />
    </div>
  );
}
